import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Pagination from "@mui/material/Pagination";
import Flatpickr from "react-flatpickr";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import { branchSwitchContext } from "../contexts/BranchSwitchProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import { fetchInvoices } from "../services/invoiceService";
import GenerateInvoice from "../utils/GenerateInvoice";
import ExportInvoices from "./ExportInvoices";
import { formatDecimal } from "../utils/decimalFormat";

const AllInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const { branchSwitch } = useContext(branchSwitchContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const limit = 7;

  const getInvoices = useCallback(async () => {
    setIsLoading(true);
    let startDate = dateRange[0]
      ? moment(dateRange[0]).format("YYYY-MM-DD")
      : null;
    let endDate = dateRange[1]
      ? moment(dateRange[1]).format("YYYY-MM-DD")
      : null;
    const response = await fetchInvoices(
      page,
      limit,
      search,
      startDate,
      endDate
    );
    if (response.data.code === 1) {
      setInvoices(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading, dateRange]);

  const resetFilter = () => {
    setPage(1);
    setSearch("");
    setDateRange([null, null]);
  };

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getInvoices();
  }, [page, search, getInvoices, branchSwitch]);

  const renderInvoiceList = (invoices) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {invoices?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Order Id</th>
                    <th scope="col">Driver</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Order Amount</th>
                    <th scope="col">Fee</th>
                    <th scope="col">Payment Type</th>
                    <th scope="col">Transaction Id</th>
                    <th scope="col">Vehicle Type</th>
                    <th scope="col">Order Date</th>
                    <th className="text-center" scope="col">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices?.map((invoice, index) => (
                    <tr key={index}>
                      <td>{invoice?.orderId}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="lh-1">
                            <span className="avatar avatar-md avatar-rounded">
                              <img
                                src={`${process.env.REACT_APP_API_PUBLIC_URL}${invoice?.driverPhoto}`}
                                alt={invoice?.driverName}
                              />
                            </span>
                          </div>
                          <div>
                            <p className="fw-medium fs-13 mb-0 d-flex align-items-center">
                              {invoice?.driverName}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div>
                            <p className="fw-medium fs-13 mb-0 d-flex align-items-center">
                              {invoice?.customerName}
                            </p>
                            <p className="fs-12 text-muted mb-0">
                              {invoice?.customerPhone}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{invoice?.orderAmount}</td>
                      <td>
                        <div className="badge bg-success-transparent">
                          {formatDecimal(invoice?.feeInt)} KWD
                        </div>
                      </td>
                      <td>{invoice?.paymentType}</td>
                      <td>{invoice?.transactionId}</td>
                      <td>{invoice?.vehicleType}</td>
                      <td>
                        {moment(invoice?.orderDateTime)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY")}
                      </td>
                      <td className="text-center">
                        <GenerateInvoice
                          orderId={invoice?._id}
                          btnWidth={100}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="10">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Manage Invoices
                        </span>
                      </div>
                    </div>
                    <div className="d-block d-md-flex flex-wrap gap-2">
                      <div>
                        <input
                          className="form-control form-control-md mb-2"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div>
                        <Flatpickr
                          className="form-control form-control-md mb-2"
                          placeholder="Search by date range"
                          options={{ mode: "range", dateFormat: "d-m-Y" }}
                          value={dateRange}
                          onChange={(dates) => {
                            if (dates.length === 2) {
                              setDateRange(dates);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={resetFilter}
                          className="btn btn-danger btn-md w-100 w-md-auto mb-2"
                        >
                          <i className="ri-tools-line me-1 fw-medium align-middle"></i>
                          Reset
                        </button>
                      </div>
                      <div>
                        <ExportInvoices search={search} dateRange={dateRange} />
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {renderInvoiceList(invoices)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default AllInvoices;
